import { Alert, Button, Card, CardContent, Input, Spinner, cn } from '@blink/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Camera, Check, CircleCheck } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ErrorAlert, Screen } from '@/components/common';
import { aed } from '@/components/parcel-bits';
import { currentLocation } from '@/components/route-bits';
import { ManualAwb } from '@/components/manual-awb';
import { Scanner, type ScanSource } from '@/components/scanner';
import { doorTone, scanFeedback } from '@/components/scan-feedback';
import { SwipeToConfirm } from '@/components/swipe';
import { useI18n } from '@/i18n';
import { api, type DeliverAnswer, type DeliveryView, type DoorScanAnswer, type PaymentMethod, type PhotoAnswer, type PodKind } from '@/lib/api';
import { newActionId } from '@/lib/ids';
import { useLiveUpdates } from '@/lib/live';
import { isNetworkError, sendOrKeep, useOutbox } from '@/lib/outbox';
import { prepareProofPhoto } from '@/lib/photo';
import { HelpTip } from '@/components/help-tip';

const POD_KINDS: PodKind[] = ['POD_EMIRATES_ID', 'POD_VILLA', 'POD_DOOR'];
const TONE: Record<DoorScanAnswer['code'], 'success' | 'warning' | 'destructive'> = {
  SCANNED: 'success',
  ALREADY_SCANNED: 'warning',
  OTHER_PARCEL: 'destructive',
  SCAN_PIECE_LABELS: 'warning',
  UNKNOWN: 'destructive',
};

/** "12.5" → 1250 fils; null when it is not an amount. */
function toFils(text: string): number | null {
  const t = text.trim().replace(',', '.');
  if (!/^\d{1,6}(\.\d{0,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

/**
 * Screen 4, Delivered (plan 4.5): scan every piece at the door, one proof photo of the chosen
 * type, cash with change or a payment-link reference, then swipe to confirm.
 */
export function Deliver() {
  const { t, f, formatTime } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  useLiveUpdates();
  // kept: saved on the phone (weak signal), sent when the signal is back
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState<(DeliverAnswer & { kept?: boolean }) | null>(null);
  const view = useQuery({
    queryKey: ['courier', 'delivery', id],
    queryFn: () => api<DeliveryView>(`/courier/parcels/${id}/delivery`),
    retry: false,
    // sending or saved: the parcel is leaving the route; a reload (the live update can come first) would answer "not found"
    enabled: !done && !sending,
  });
  const v = view.data;

  const [lastScan, setLastScan] = useState<DoorScanAnswer | null>(null);
  const [kind, setKind] = useState<PodKind | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [cash, setCash] = useState('');
  const [linkRef, setLinkRef] = useState('');
  // one ID per delivery, made when the screen opens: a repeat send is counted once
  const [actionId] = useState(newActionId);

  // what is still on the phone for this parcel (weak signal)
  const kept = useOutbox().filter((x) => x.shipmentId === id && x.state === 'WAITING');
  const keptScans = new Set(kept.filter((x) => x.type === 'DOOR_SCAN').map((x) => x.body.barcode as string));
  const keptPhoto = kept.filter((x) => x.type === 'PHOTO' && POD_KINDS.includes(x.body.kind as PodKind)).at(-1);
  const pieces = (v?.pieces ?? []).map((p) => ({ ...p, scanned: p.scanned || keptScans.has(p.barcode) }));

  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);
  useEffect(() => {
    if (v && !method && v.paymentMethods.length) setMethod(v.paymentMethods[0]!);
    if (v?.photo && !kind) setKind(v.photo.kind);
  }, [v, method, kind]);

  const scan = useMutation({
    mutationFn: async (s: { barcode: string; source: ScanSource }): Promise<DoorScanAnswer> => {
      try {
        return await api<DoorScanAnswer>(`/courier/parcels/${id}/door-scan`, { method: 'POST', body: s });
      } catch (e) {
        if (!isNetworkError(e) || !v) throw e;
        // weak signal: check the label against this parcel's pieces on the phone, send it later
        const barcode = s.barcode.trim().toUpperCase();
        const piece = pieces.find((p) => p.barcode === barcode);
        if (!piece) return { code: barcode === v.awb && pieces.length > 1 ? 'SCAN_PIECE_LABELS' : 'UNKNOWN', barcode, offline: true };
        if (piece.scanned) return { code: 'ALREADY_SCANNED', barcode, piece: piece.label, offline: true };
        await sendOrKeep({ id: newActionId(), type: 'DOOR_SCAN', shipmentId: id!, awb: v.awb, body: { barcode, source: s.source } });
        return { code: 'SCANNED', barcode, piece: piece.label, offline: true };
      }
    },
    onSuccess: (answer) => {
      scanFeedback(doorTone(answer.code));
      setLastScan(answer);
      void queryClient.invalidateQueries({ queryKey: ['courier', 'delivery', id] });
    },
    onError: () => scanFeedback('error'),
  });
  const photo = useMutation({
    mutationFn: async (file: File) => {
      const prepared = await prepareProofPhoto(file);
      setPreview(URL.createObjectURL(prepared.blob));
      return sendOrKeep<PhotoAnswer>({ id: newActionId(), type: 'PHOTO', shipmentId: id!, awb: v!.awb, body: { kind, ...prepared.fields }, blob: prepared.blob });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['courier', 'delivery', id] }),
  });

  const cod = v?.codFils ?? 0;
  const cashFils = toFils(cash);
  const piecesDone = !!v && pieces.every((p) => p.scanned);
  const photoDone = !!v?.photo || !!keptPhoto;
  const paymentDone = cod === 0 || (method === 'CASH' ? cashFils != null && cashFils >= cod : method === 'PAYMENT_LINK' ? /^[A-Za-z0-9#/._-]{3,60}$/.test(linkRef.trim()) : false);

  const deliver = useMutation({
    mutationFn: async (): Promise<DeliverAnswer & { kept?: boolean }> => {
      const payment = cod === 0 ? null : method === 'CASH' ? { method, receivedFils: cashFils } : { method, linkReference: linkRef.trim() };
      const body = {
        clientActionId: actionId,
        payment,
        location: await currentLocation(4000),
        // a photo still on the phone is named by its own ID until it is sent
        ...(keptPhoto ? { photoClientId: keptPhoto.id, photoField: 'podEvidenceId' } : { podEvidenceId: v!.photo!.id }),
      };
      const result = await sendOrKeep<DeliverAnswer>({ id: actionId, type: 'DELIVER', shipmentId: id!, awb: v!.awb, body });
      if (result.sent) return result.answer;
      const link = cod > 0 && method === 'PAYMENT_LINK';
      return { kept: true, deliveryId: actionId, status: link ? 'DELIVERED_LINK_PENDING' : 'DELIVERED', paymentMethod: payment?.method ?? null, codFils: cod, changeFils: cod > 0 && method === 'CASH' ? cashFils! - cod : null };
    },
    onMutate: () => setSending(true),
    onError: () => setSending(false),
    onSuccess: (answer) => {
      setDone(answer);
      void queryClient.invalidateQueries({ queryKey: ['courier'] });
    },
  });

  const back = (
    <Button variant="ghost" size="icon" className="order-first text-white hover:bg-white/10 hover:text-white" aria-label={t.parcel.back} onClick={() => navigate(`/parcels/${id}`)} data-testid="back">
      <ArrowLeft className="h-5 w-5" />
    </Button>
  );
  const title = <h1 className="truncate font-mono text-lg font-semibold">{v?.awb ?? '…'}</h1>;

  if (done) {
    return (
      <Screen title={title}>
        <Card data-testid="delivered">
          <CardContent className="space-y-3 pt-6 text-center">
            <CircleCheck className="mx-auto h-12 w-12 text-success" />
            <p className="text-lg font-semibold">{done.status === 'DELIVERED_LINK_PENDING' ? t.deliver.doneLink : t.deliver.done}</p>
            {done.kept ? (
              <p className="rounded-lg bg-muted p-3 text-sm" data-testid="delivered-kept">
                {t.outbox.keptDelivery}
              </p>
            ) : null}
            {done.changeFils ? (
              <p className="rounded-lg bg-warning-soft p-3 text-base font-semibold" data-testid="give-change">
                {f(t.deliver.giveChange, { amount: aed(done.changeFils) })}
              </p>
            ) : null}
            <Button className="h-12 w-full text-base" onClick={() => navigate('/', { replace: true })} data-testid="next-stop">
              {t.deliver.nextStop}
            </Button>
          </CardContent>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen title={title} actions={back}>
      {view.isLoading ? <Spinner /> : null}
      <ErrorAlert error={view.error} />
      {v ? (
        <>
          <Step n={1} title={t.deliver.scanTitle} done={piecesDone} help="courier.deliver">
            <p className="text-sm font-semibold" data-testid="door-progress">
              {f(t.deliver.scanProgress, { scanned: pieces.filter((p) => p.scanned).length, total: pieces.length })}
            </p>
            {pieces.length > 1 ? (
              <div className="flex flex-wrap gap-1.5">
                {pieces.map((p) => (
                  <span key={p.label} className={cn('rounded-md px-2 py-0.5 font-mono text-xs', p.scanned ? 'bg-success text-white' : 'bg-muted text-muted-foreground')}>
                    {p.label}
                  </span>
                ))}
              </div>
            ) : null}
            {!piecesDone ? <Scanner busy={scan.isPending} onScan={(barcode, source) => scan.mutate({ barcode, source })} /> : null}
            {lastScan ? (
              <Alert variant={TONE[lastScan.code]} data-testid="door-answer" data-code={lastScan.code}>
                <span className="font-mono">{lastScan.piece ?? lastScan.barcode} — </span>
                {t.deliver.answer[lastScan.code]}
                {lastScan.offline ? <span className="block text-xs">{t.outbox.scanKept}</span> : null}
              </Alert>
            ) : null}
            <ErrorAlert error={scan.error} />
            {!piecesDone ? <ManualAwb shipmentId={v.id} pieces={pieces.filter((p) => !p.scanned).map((p) => p.label)} manual={v.manual} /> : null}
          </Step>

          <Step n={2} title={t.deliver.photoTitle} done={photoDone}>
            <div className="grid grid-cols-3 gap-2" role="radiogroup">
              {POD_KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={kind === k}
                  onClick={() => setKind(k)}
                  className={cn('rounded-lg border px-2 py-2 text-xs font-semibold', kind === k ? 'border-primary bg-primary text-primary-foreground' : 'bg-card')}
                  data-testid={`pod-${k}`}
                >
                  {t.deliver.podKinds[k]}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{kind === 'POD_EMIRATES_ID' ? t.deliver.photoHintId : t.deliver.photoHint}</p>
            <label className={cn('flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-md border text-base font-semibold', !kind || photo.isPending ? 'pointer-events-none opacity-50' : 'bg-card')}>
              <Camera className="h-5 w-5" />
              {photo.isPending ? t.deliver.photoSaving : photoDone ? t.deliver.retake : t.deliver.takePhoto}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                disabled={!kind || photo.isPending}
                data-testid="pod-photo"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) photo.mutate(file);
                }}
              />
            </label>
            {preview ? <img src={preview} alt="" className="max-h-48 w-full rounded-lg object-cover" /> : null}
            {keptPhoto ? (
              <p className="flex items-center gap-1 text-xs text-warning-foreground" data-testid="photo-kept">
                <Check className="h-4 w-4" />
                {f(t.outbox.photoKept, { kind: t.deliver.podKinds[keptPhoto.body.kind as PodKind] })}
              </p>
            ) : v.photo ? (
              <p className="flex items-center gap-1 text-xs text-success" data-testid="photo-saved">
                <Check className="h-4 w-4" />
                {f(t.deliver.photoSaved, { kind: t.deliver.podKinds[v.photo.kind], time: formatTime(v.photo.takenAt) })}
              </p>
            ) : null}
            <ErrorAlert error={photo.error} />
          </Step>

          {cod > 0 ? (
            <Step n={3} title={f(t.deliver.paymentTitle, { amount: aed(cod) })} done={paymentDone}>
              <div className="flex gap-2">
                {v.paymentMethods.map((m) => (
                  <Button key={m} variant={method === m ? 'default' : 'outline'} className="h-11 flex-1" onClick={() => setMethod(m)} data-testid={`pay-${m}`}>
                    {t.deliver.methods[m]}
                  </Button>
                ))}
              </div>
              {method === 'CASH' ? (
                <div className="space-y-2">
                  <label className="text-sm font-semibold" htmlFor="cash">{t.deliver.cashReceived}</label>
                  <div className="flex gap-2">
                    <Input id="cash" inputMode="decimal" className="h-12 text-lg" value={cash} onChange={(e) => setCash(e.target.value)} placeholder={(cod / 100).toFixed(2)} data-testid="cash-received" />
                    <Button variant="outline" className="h-12" onClick={() => setCash((cod / 100).toFixed(2))} data-testid="cash-exact">
                      {t.deliver.exact}
                    </Button>
                  </div>
                  {cashFils != null && cashFils >= cod ? (
                    <p className="text-sm font-semibold" data-testid="change">{f(t.deliver.change, { amount: aed(cashFils - cod) })}</p>
                  ) : cash ? (
                    <p className="text-sm text-destructive">{f(t.deliver.cashShort, { amount: aed(cod) })}</p>
                  ) : null}
                </div>
              ) : method === 'PAYMENT_LINK' ? (
                <div className="space-y-2">
                  <label className="text-sm font-semibold" htmlFor="link">{t.deliver.linkRef}</label>
                  <Input id="link" className="h-12 font-mono" value={linkRef} onChange={(e) => setLinkRef(e.target.value)} maxLength={60} autoCapitalize="characters" data-testid="link-ref" />
                  <p className="text-xs text-muted-foreground">{t.deliver.linkHint}</p>
                </div>
              ) : null}
            </Step>
          ) : (
            <p className="rounded-lg bg-success-soft px-4 py-3 text-sm font-semibold text-success">{t.deliver.prepaid}</p>
          )}

          <div className="sticky bottom-0 -mx-4 space-y-2 border-t bg-background/95 p-4 backdrop-blur">
            {!piecesDone || !photoDone || !paymentDone ? (
              <p className="text-xs text-muted-foreground" data-testid="still-missing">
                {t.deliver.missing} {[!piecesDone && t.deliver.missingPieces, !photoDone && t.deliver.missingPhoto, !paymentDone && t.deliver.missingPayment].filter(Boolean).join(' · ')}
              </p>
            ) : null}
            <ErrorAlert error={deliver.error} />
            {/* a new handle after a refused send, so the courier can swipe again */}
            <SwipeToConfirm key={deliver.failureCount} label={deliver.isPending ? t.deliver.saving : t.deliver.swipe} disabled={!piecesDone || !photoDone || !paymentDone || deliver.isPending} onConfirm={() => deliver.mutate()} />
          </div>
        </>
      ) : null}
    </Screen>
  );
}

function Step({ n, title, done, help, children }: { n: number; title: string; done: boolean; help?: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-xl border bg-card p-4" data-testid={`step-${n}`} data-done={done}>
      <h2 className="flex items-center gap-2 font-semibold">
        <span className={cn('flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold', done ? 'bg-success text-white' : 'bg-muted text-muted-foreground')}>
          {done ? <Check className="h-4 w-4" /> : n}
        </span>
        {title}
        {help ? <HelpTip id={help} className="text-muted-foreground" /> : null}
      </h2>
      {children}
    </section>
  );
}
